import { useMutation } from '@tanstack/react-query'
import { Download, Landmark, Scale } from 'lucide-react'
import { useState } from 'react'

import {
  exportCreditBureauExtract,
  exportPrudentialReturn,
  type ExtractFormat,
  type PrudentialSchedule,
} from '@/api/compliance-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { saveBlobAsFile } from '@/lib/download'
import { resolveErrorMessage } from '@/lib/error-messages'

import { today } from './constants'
import { useReportsPermissions } from './permissions'

/**
 * The regulatory extracts — prudential return schedules and the credit-bureau file.
 *
 * Served by the Compliance module, gated on the reports family's own codes
 * (`reports.regulatory.generate`, `reports.bureau.extract`), and honest about their standing:
 * every prudential figure rests on assumptions the compliance officer has not fully confirmed (see
 * the assumptions register), filing remains a manual step through ORASS, and the bureau file's
 * layout has not been validated against the contracted bureau's specification. Producing the file
 * is the part software does; nothing here claims the rest.
 */

const SCHEDULES: { value: PrudentialSchedule; label: string; detail: string }[] = [
  { value: 'Capital', label: 'Capital adequacy', detail: 'Core capital against risk-weighted assets.' },
  { value: 'Liquidity', label: 'Liquidity', detail: 'Liquid assets against deposit liabilities.' },
  {
    value: 'AssetQuality',
    label: 'Asset quality',
    detail: 'The classified portfolio by band, with provisions.',
  },
]

const FORMATS: ExtractFormat[] = ['Csv', 'Xlsx']

export function RegulatoryExtractsSection() {
  const permissions = useReportsPermissions()

  if (!permissions.canGenerateRegulatory && !permissions.canExtractBureau) {
    return null
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-heading">Regulatory extracts</h2>
      <div className="grid gap-4 lg:grid-cols-2">
        {permissions.canGenerateRegulatory ? <PrudentialReturnCard /> : null}
        {permissions.canExtractBureau ? <CreditBureauCard /> : null}
      </div>
    </section>
  )
}

function PrudentialReturnCard() {
  const [schedule, setSchedule] = useState<PrudentialSchedule>('AssetQuality')
  const [asOf, setAsOf] = useState(today)

  const mutation = useMutation({
    mutationFn: exportPrudentialReturn,
    onSuccess: ({ blob, filename }) => {
      saveBlobAsFile(blob, filename)
    },
  })

  const chosen = SCHEDULES.find((candidate) => candidate.value === schedule)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="size-4 text-muted" aria-hidden />
          Prudential return schedules
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="prudential-schedule">Schedule</Label>
            <Select value={schedule} onValueChange={(next) => setSchedule(next as PrudentialSchedule)}>
              <SelectTrigger id="prudential-schedule">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEDULES.map((candidate) => (
                  <SelectItem key={candidate.value} value={candidate.value}>
                    {candidate.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen ? <p className="text-2xs text-muted">{chosen.detail}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="prudential-as-of">As at</Label>
            <Input
              id="prudential-as-of"
              type="date"
              value={asOf}
              max={today()}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((format) => (
            <Button
              key={format}
              variant="secondary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ schedule, asOf, format })}
            >
              <Download />
              {mutation.isPending && mutation.variables?.format === format
                ? 'Building…'
                : format === 'Csv'
                  ? 'CSV'
                  : 'Excel'}
            </Button>
          ))}
          {mutation.isError ? (
            <span className="text-xs text-danger-text">{resolveErrorMessage(mutation.error)}</span>
          ) : null}
        </div>

        <p className="text-2xs text-muted">
          The minimums these schedules check against are provisional until the compliance officer
          confirms them — the assumptions register says which. Filing is a manual step through ORASS;
          this produces the figures, not the filing.
        </p>
      </CardContent>
    </Card>
  )
}

function CreditBureauCard() {
  const [asOf, setAsOf] = useState(today)

  const mutation = useMutation({
    mutationFn: exportCreditBureauExtract,
    onSuccess: ({ blob, filename }) => {
      saveBlobAsFile(blob, filename)
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark className="size-4 text-muted" aria-hidden />
          Credit bureau extract
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="bureau-as-of">As at</Label>
          <Input
            id="bureau-as-of"
            type="date"
            value={asOf}
            max={today()}
            onChange={(event) => setAsOf(event.target.value)}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((format) => (
            <Button
              key={format}
              variant="secondary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ asOf, format })}
            >
              <Download />
              {mutation.isPending && mutation.variables?.format === format
                ? 'Building…'
                : format === 'Csv'
                  ? 'CSV'
                  : 'Excel'}
            </Button>
          ))}
          {mutation.isError ? (
            <span className="text-xs text-danger-text">{resolveErrorMessage(mutation.error)}</span>
          ) : null}
        </div>

        <p className="text-2xs text-muted">
          One row per loan cycle, as the data model holds it. The column layout has not been
          validated against the contracted bureau&apos;s file specification — assumptions register
          item A9 — so treat the first submission as a conversation with the bureau, not a filing.
        </p>
      </CardContent>
    </Card>
  )
}
